import {
    Box,
    Button,
    Card,
    LinearProgress,
    Typography,
    TypographyProps,
    styled
} from "@mui/material";

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TeamType } from "../../models";
import { format } from "date-fns";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { graphQLStripeClient } from "../../lib/stripe/GraphQLStripeClient";
import { usePageDefaultStyle } from "../../assets/style/usePageDefaultStyle";
import { useSnackbar } from "notistack";
import { useSubscriptionDetails } from "../../lib/subscription/detail/useSubscriptionDetails";
import { useTeamManagement } from "../teamManagement/useTeamManagement";
import { useUser } from "../../components/user/UserContext";

const Header = styled(Typography)<TypographyProps>(() => ({
    fontSize: "1.25rem",
    fontWeight: 500
}));

const FieldLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
    fontSize: "0.875rem"
}));

const FieldValue = styled(Typography)<TypographyProps>(() => ({
    fontSize: "1.25rem"
}));

export const UserProfile = () => {
    const { pageDefaultStyle } = usePageDefaultStyle();
    const { userAttributes } = useUser();
    const { subscriptionDetail, getSubscriptionDetailStatus } = useSubscriptionDetails();
    const snackbar = useSnackbar();
    const { organization, userRole } = useTeamManagement();

    const getStripeProfileUrl = async () => {
        try {
            const result = await graphQLStripeClient.createBillingProfileSession({
                returnURL: window.location.href
            });
            if (!result.url) {
                throw new Error("Failed to create billing profile session");
            }
            window.open(result.url, "_blank");
        } catch (error) {
            snackbar.enqueueSnackbar("Failed to create billing profile session", { variant: "error" });
        }
    };
    const inTrial = subscriptionDetail?.trialEnd && subscriptionDetail?.trialEnd > Date.now() / 1000;

    return (
        <Box sx={pageDefaultStyle}>
            <Header mb={3}>Profile Information</Header>
            <Box display="flex" flexDirection="column" gap={3}>
                <Card sx={{ py: 2, px: { xs: 2, sm: 4 } }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Header>Personal Details</Header>
                        <Box
                            display="grid"
                            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                            gap={2}
                        >
                            {userAttributes?.["given_name"] && (
                                <Box>
                                    <FieldLabel>First Name</FieldLabel>
                                    <FieldValue>{userAttributes?.["given_name"]}</FieldValue>
                                </Box>
                            )}
                            {userAttributes?.["family_name"] && (
                                <Box>
                                    <FieldLabel>Last Name</FieldLabel>
                                    <FieldValue>{userAttributes?.["family_name"]}</FieldValue>
                                </Box>
                            )}
                            {!userAttributes?.["given_name"] && userAttributes?.["name"] && (
                                <Box>
                                    <FieldLabel>Name</FieldLabel>
                                    <FieldValue>{userAttributes?.["name"]}</FieldValue>
                                </Box>
                            )}
                            <Box>
                                <FieldLabel>Phone Number</FieldLabel>
                                <FieldValue>{formatPhoneNumberIntl(userAttributes?.["phone_number"] ?? "")}</FieldValue>
                            </Box>
                            <Box>
                                <FieldLabel>Email</FieldLabel>
                                <FieldValue>{userAttributes?.["email"]}</FieldValue>
                            </Box>
                        </Box>
                    </Box>
                </Card>
                {userRole && (
                    <Card sx={{ py: 2, px: { xs: 2, sm: 4 } }}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Header>Organization Details</Header>
                            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}>
                                <Box display="flex" flexDirection="column">
                                    <FieldLabel>Organization</FieldLabel>
                                    <FieldValue>{organization?.legalName}</FieldValue>
                                </Box>
                                <Box display="flex" flexDirection="column">
                                    <FieldLabel>Role</FieldLabel>
                                    <FieldValue>{userRole}</FieldValue>
                                </Box>
                            </Box>
                        </Box>
                    </Card>
                )}
                {userRole === TeamType.OWNER && (
                    <Card sx={{ py: 2, px: { xs: 2, sm: 4 } }}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                                <Header>Subscription</Header>
                                {getSubscriptionDetailStatus === "success" && (
                                    <Button
                                        onClick={getStripeProfileUrl}
                                        variant="contained"
                                        color="primary"
                                        endIcon={<OpenInNewIcon />}
                                    >
                                        Manage Payment Details
                                    </Button>
                                )}
                            </Box>
                            {getSubscriptionDetailStatus === "loading" && (
                                <LinearProgress />
                            )}
                            {getSubscriptionDetailStatus === "error" && (
                                <Typography color="error">Failed to load subscription details</Typography>
                            )}
                            {getSubscriptionDetailStatus === "success" && (
                                subscriptionDetail ? (
                                    <>
                                        <Box>
                                            <FieldLabel>Plan</FieldLabel>
                                            <FieldValue>{subscriptionDetail?.plan}</FieldValue>
                                        </Box>
                                        <Box>
                                            <FieldLabel>Status</FieldLabel>
                                            <FieldValue>{inTrial ? "Trial" : "Active"}</FieldValue>
                                        </Box>
                                        {inTrial && (
                                            <Box>
                                                <FieldLabel>Trial End</FieldLabel>
                                                <FieldValue>{format(subscriptionDetail.trialEnd! * 1000, "MMMM d, yyyy")}</FieldValue>
                                            </Box>
                                        )}
                                        {!inTrial && (
                                            <Box>
                                                <FieldValue fontWeight={500}>Usage</FieldValue>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <FieldValue fontWeight={500}>{subscriptionDetail?.meteringUsage ?? 0}</FieldValue>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={subscriptionDetail?.meteringUsage && subscriptionDetail?.meteringLimit
                                                            ? (subscriptionDetail!.meteringUsage / subscriptionDetail!.meteringLimit) * 100
                                                            : 0}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            backgroundColor: theme => theme.palette.divider,
                                                            maxWidth: theme => `calc(${theme.breakpoints.values.sm}px - ${theme.spacing(15)})`,
                                                            flex: 1
                                                        }}
                                                    />
                                                    <FieldValue fontWeight={500}>{subscriptionDetail?.meteringLimit ?? 0} Assessments</FieldValue>
                                                </Box>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Typography color="textSecondary">No active subscription found</Typography>
                                )
                            )}
                        </Box>
                    </Card>
                )}
            </Box>
        </Box>
    );
};